import Woman from "../../assets/images/contact-section4.webp";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  useTheme,
} from "@mui/material";
import { Stack, styled } from "@mui/system";
import { useState } from "react";
import ReadyWhenYouAre from "../../components/BookConsultationForm";
import { useNavigate } from "react-router-dom";
import CtaButton from "../../components/CtaButton";
import Pill from "../../components/Pill";
import { CONSULTATION_COPY } from "../../constants/consultation";
import { openSimplePracticeClientPortal } from "../../utils/simplePractice";

const CARD = "#FFFFFF"; // accordion surface
const STROKE = "#BCAFA7"; // soft brown border
const DOT = "#2D2018"; // dark pill for + / − button

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#EAE6DA",
  color: "#4c2e1f",
  padding: "20px 20px",
  //paddingBottom: "2%",
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
    //padding: "30px 10px",
  },
}));

function DashboardCard({
  title,
  description,
  reverse = false,
  subTitle,
  number,
}) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 6,
        bgcolor: "#FFFFFF",
        height: "200px",
      }}
    >
      {/* Video + Description */}
      <Stack
        direction={{ xs: "column", md: reverse ? "row-reverse" : "row" }}
        spacing={{ xs: 2, sm: 3, md: 4 }}
        sx={{ p: 1, height: "100%" }}
        alignItems="center"
      >
        <Box
          sx={{
            flex: 1,
            width: "100px",
            height: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 2,
              backgroundColor: "#EAE6DA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                ...theme.typography.heading,
                fontWeight: 300,
              }}
            >
              {number}
            </Typography>
          </Box>
        </Box>

        {/* Text content */}
        <Box
          sx={{
            flex: 2,
            width: "100%",
            pr: reverse ? 0 : 2,
            pl: reverse ? 2 : 0,
          }}
        >
          <Typography
            sx={{
              ...theme.typography.heading,
              fontWeight: 400,
              fontSize: { xs: 18, sm: 24, md: 32 },
            }}
          >
            {title}
          </Typography>
          {subTitle && (
            <Typography
              sx={{
                ...theme.typography.heading,
              }}
            >
              {subTitle}
            </Typography>
          )}
          <Typography
            sx={{
              color: "rgba(46,36,30,0.75)",
              fontSize: { xs: 14, sm: 15.5 },
              lineHeight: 1.6,
            }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const BookConsultation = () => {
  const [expanded, setExpanded] = useState("0");
  const theme = useTheme();
  const navigate = useNavigate();
 const faqs = [
   {
     q: "Is the consultation truly free?",
     a: "Yes. It's a complimentary call designed to help you explore whether Nirva is a good fit. During the consultation, your practitioner will lovingly assess your needs to determine if they can help. If the practitioner feels they are not the right fit for you, they will connect you with someone in our network who may be better suited to your goals or preferences.",
   },
   {
     q: "How long is it?",
     a: CONSULTATION_COPY.durationOnline,
   },
   {
     q: "Do I need to prepare?",
     a: "No special preparation is needed. During your initial inquiry, you'll provide details about your preferred practitioner (gender, age range, time zone, spiritual inclinations) and the issue you wish to work on. This helps us match you with the best practitioner for your needs.",
   },
   {
     q: "What happens after?",
     a: "If you and your practitioner are a good fit, you can schedule your first session, which typically can be booked within a few days. Initial sessions cost $90 (1.5 clock hours) and focus on comprehensive intake, goal setting, and hypnotic programming. Each clock hour includes 50 minutes of dedicated session time within a 60-minute scheduling block. You can continue working with your practitioner for as long as you'd like—many clients address their primary concern within 2 to 4 sessions.",
   },
   {
     q: "Is Nirva right for me?",
     a: "Nirva is perfect if you're seeking quality online hypnotherapy, affordable services, compassionate practitioners, and integrity and professionalism. Our practitioners are certified through the Institute of Interpersonal Hypnotherapy with over 300-500 hours of training, are HIPAA-compliant, insured, and have undergone thorough background checks. The process is completely risk-free—start with a 30-minute free consultation to find out if hypnotherapy is right for you.",
   },
 ];
 const handleChange = (panel) => (_e, isExp) => {
   setExpanded(isExp ? panel : false);
 };
 return (
   <Box className="homepage" sx={{ backgroundColor: "#EAE6DA" }}>
     <HeroSection>
       <Box
         sx={{
           width: "100%",
           bgcolor: "transparent",
           color: "text.primary",
           py: { xs: 8, md: 12 },
         }}
       >
         <Container
           maxWidth="lg"
           sx={{
             textAlign: "center",
             display: "flex",
             flexDirection: "column",
             alignItems: "center",
             justifyContent: "center",
             gap: 5,
           }}
         >
           <Typography sx={theme.typography.overHeading}>
             {CONSULTATION_COPY.bookYour}
           </Typography>

           <Typography component="h1" sx={theme.typography.heading}>
             Beautiful Thinking. Peaceful Living.
           </Typography>

           <Typography
             sx={{
               ...theme.typography.subtext,
               fontSize: { xs: 14, sm: 16, md: 28 },
               fontWeight: 600,
               fontStyle: "semibold",
             }}
           >
             Begin with a conversation.
           </Typography>

           <Typography sx={theme.typography.subtext}>{CONSULTATION_COPY.intro}</Typography>

           <Stack
             direction={{ xs: "column", md: "row" }}
             spacing={2}
             justifyContent="center"
             sx={{ flexWrap: "wrap" }}
           >
             <CtaButton
              text={CONSULTATION_COPY.book}
               onClick={() => {
                 navigate("/");
               }}
             />
           </Stack>
         </Container>
       </Box>
     </HeroSection>

     <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "#EAE6DA" }}>
       <Container maxWidth="lg">
         <Box
           sx={{
             display: "grid",
             gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
             gap: { xs: 4, md: 8 },
             alignItems: "center",
           }}
         >
           {/* Left Column */}
           <Stack spacing={2.5}>
             <Typography
               sx={{
                 ...theme.typography.heading,
                 textAlign: { xs: "center", md: "left" },
               }}
             >
               What You Can Expect
             </Typography>

             <Typography
               sx={{
                 ...theme.typography.subtext,
                 textAlign: { xs: "center", md: "left" },
               }}
             >
               While experiences vary from person to person, many clients
               describe hypnotherapy as calming, clarifying, and practically
               helpful in everyday life.
             </Typography>

             <Box
               sx={{
                 pt: 1,
                 display: "flex",
                 alignItems: "center",
                 justifyContent: { xs: "center", md: "start" },
               }}
             >
               <CtaButton
                 text={CONSULTATION_COPY.book}
                 onClick={() => {
                   navigate("/");
                 }}
               />
             </Box>
           </Stack>

           {/* Right Column */}
           <Stack spacing={1.5}>
             <Pill>
               <strong>A caring, confidential conversation</strong> focused on
               your needs and intentions
             </Pill>
             <Pill>
               <strong>Clarity on how hypnotherapy works</strong> and what a
               first session looks like
             </Pill>
             <Pill>
               <strong>Personalized guidance</strong> tailored to your
               goals—such as stress reduction, confidence building, habit
               change, purpose clarification, and more.
             </Pill>
             <Pill>
               <strong>Simple next steps</strong> if you choose to proceed
             </Pill>
           </Stack>
         </Box>
       </Container>
     </Box>

     <Box sx={{ py: { xs: 6, md: 10 } }}>
       <Container
         maxWidth="md"
         sx={{
           display: "flex",
           flexDirection: "column",
           alignItems: "center",
           justifyContent: "center",
           gap: 2,
         }}
       >
         <Typography sx={theme.typography.heading}>How We Work</Typography>
         <Typography
           variant="h3"
           sx={{
             ...theme.typography.subtext,
             textAlign: "center",
             mb: { xs: 3, md: 5 },
             fontWeight: 600,
             fontSize: { xs: 14, sm: 18, md: 22 },
           }}
         >
           At a glance (full journey):
         </Typography>
         <Typography
           variant="h3"
           sx={{
             ...theme.typography.subtext,
             fontWeight: 400,
             fontSize: { xs: 14, sm: 18, md: 22 },
           }}
         >
          {CONSULTATION_COPY.journeyStep}
         </Typography>

         <Stack spacing={{ xs: 3, md: 4 }} sx={{ mt: 6 }}>
           <Typography
             variant="h3"
             sx={{
               ...theme.typography.subtext,
               textAlign: "center",
               fontWeight: 700,
               fontSize: { xs: 14, sm: 18, md: 22 },
             }}
           >
             Step-by-step:
           </Typography>

           <DashboardCard
             title="Intake Questionnaire"
             description="Share a few details so we can thoughtfully match you with the right practitioner."
             number="1"
           />

           <DashboardCard
             title={CONSULTATION_COPY.bookA}
             description="Choose a time for a brief, confidential conversation to clarify goals and next steps."
             reverse
             number="2"
           />

           <DashboardCard
             title="Book Your Session"
             description="If it feels like a fit, select your first appointment time."
             number="3"
           />
           <DashboardCard
             title="Client Participation Agreement"
             description="Complete this simple form so we can proceed with clarity and mutual understanding."
             number="4"
             reverse
           />
           <DashboardCard
             title="Session(s)"
             description="Build awareness, soothe reactivity, and create space for thoughtful responses."
             number="5"
           />
           <DashboardCard
             title="Follow-Up"
             description="Receive personalized recommendations, optional resources, and support for what’s next."
             number="6"
             reverse
           />

           <Typography
             sx={{
               ...theme.typography.subtext,
               textAlign: "center",
               fontSize: 22,
               fontWeight: 400,
             }}
           >
             <strong>Response time:</strong> We reply within{" "}
             <strong>24 hours on weekdays</strong> and{" "}
             <strong>by the next business day</strong> for weekend requests.
           </Typography>

           <Stack
             direction={{ xs: "column", md: "row" }}
             spacing={2}
             justifyContent="center"
             sx={{ flexWrap: "wrap" }}
           >
             <CtaButton
               text={CONSULTATION_COPY.book}
               onClick={() => {
                 navigate("/");
               }}
             />
           </Stack>
         </Stack>
       </Container>
     </Box>

     <Box sx={{ width: "100%", py: { xs: 6, md: 10 } }}>
       <Container maxWidth="lg">
         <Paper
           elevation={0}
           sx={{
             bgcolor: "#FFF",
             borderRadius: 4,
             p: { xs: 3, sm: 4, md: 4 },
             boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
           }}
         >
           <Box
             sx={{
               display: "grid",
               gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
               gap: { xs: 4, md: 6 },
               alignItems: "center",
             }}
           >
             {/* Left Column - Image */}
             <Box
               sx={{
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 height: "100%",
               }}
             >
               <Box
                 component="img"
                 alt="Nirva – Interpersonal Hypnotherapy Clinic"
                 src={Woman}
                 sx={{
                   borderRadius: 4,
                   width: "100%",
                   height: "auto",
                   maxWidth: { xs: "90%", sm: "85%", md: "100%" },
                   objectFit: "cover",
                   boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                 }}
               />
             </Box>

             {/* Right Column - Content */}
             <Box
               sx={{
                 textAlign: { xs: "center", md: "left" },
                 px: { xs: 2, sm: 3, md: 0 },
               }}
             >
               <Typography
                 variant="h4"
                 sx={{
                   ...theme.typography.heading,
                   mb: { xs: 3, md: 4 },
                 }}
               >
                 Why People Choose Nirva
               </Typography>

               <Stack spacing={{ xs: 2, md: 2.5 }}>
                 <Pill innerPill>
                   <strong>Certified practitioners</strong> trained through the{" "}
                   <strong>
                     Institute of Interpersonal Hypnotherapy (IIH)
                   </strong>
                   , grounded in compassion and ethical practice
                 </Pill>
                 <Pill innerPill>
                   <strong>Professional standards & ethics</strong> you can
                   trust
                 </Pill>
                 <Pill innerPill>
                   <strong>A steady, respectful approach—</strong>you set the
                   pace, we support the process
                 </Pill>
                 <Pill innerPill>
                   <strong>Free resources</strong> to support you between
                   sessions
                 </Pill>
               </Stack>
             </Box>
           </Box>
         </Paper>
       </Container>
     </Box>

     <Box sx={{ py: { xs: 6, md: 8 } }}>
       <Container maxWidth="md">
         <Typography align="center" sx={theme.typography.heading}>
           Is This Right for Me?
         </Typography>
         <Typography
           align="center"
           sx={{
             ...theme.typography.heading,
             fontSize: { xs: 18, sm: 25, md: 40 },
             fontWeight: 400,
           }}
         >
           (Common Questions)
         </Typography>
         <Box>
           {faqs.map((item, idx) => {
             const key = String(idx);
             const isOpen = expanded === key;

             return (
               <Accordion
                 key={key}
                 expanded={isOpen}
                 onChange={handleChange(key)}
                 disableGutters
                 square={false}
                 sx={{
                   mb: 2,
                   borderRadius: 3,
                   background: isOpen ? "transparent" : CARD,
                   // border only when expanded
                   border: isOpen ? "2px solid" : "none",
                   borderColor: isOpen ? "black" : "transparent",
                   boxShadow: isOpen
                     ? "0 2px 0 rgba(45,32,24,0.06)"
                     : "0 1px 0 rgba(0,0,0,0.04)",
                   "&:before": { display: "none" },
                   "&.Mui-expanded": { mt: 0, borderRadius: 3 },
                 }}
               >
                 <AccordionSummary
                   sx={{
                     px: { xs: 2, md: 3 },
                     py: { xs: 1.75, md: 2 },
                     "& .MuiAccordionSummary-content": {
                       m: 0,
                       display: "flex",
                       alignItems: "center",
                     },
                     background: "transparent",
                   }}
                 >
                   <Typography
                     sx={{
                       ...theme.typography.subtext,
                       fontWeight: 700,
                       fontSize: { xs: 16, md: 20 },
                     }}
                   >
                     {idx + 1}) {item.q}
                   </Typography>

                   {/* Slightly rounded-square +/− control */}
                   <IconButton
                     edge="end"
                     onClick={(e) => {
                       e.stopPropagation();
                       setExpanded(isOpen ? false : key);
                     }}
                     sx={{
                       ml: "auto",
                       width: 38,
                       height: 38,
                       borderRadius: 3, // ~10px — "a little square"
                       background: isOpen ? DOT : "#fff",
                       color: isOpen ? "#fff" : DOT,
                       border: "1px solid",
                       borderColor: isOpen ? "transparent" : STROKE,
                       "&:hover": {
                         background: isOpen ? "#23170F" : "#fff",
                         boxShadow: isOpen
                           ? "none"
                           : "0 0 0 2px rgba(0,0,0,0.04) inset",
                       },
                     }}
                   >
                     {isOpen ? (
                       <RemoveIcon sx={{ fontSize: "20px" }} />
                     ) : (
                       <AddIcon sx={{ fontSize: "20px" }} />
                     )}
                   </IconButton>
                 </AccordionSummary>

                 <AccordionDetails
                   sx={{
                     pt: 0,
                     pb: { xs: 2.25, md: 2.75 },
                     px: { xs: 2, md: 3 },
                     background: "transparent",
                   }}
                 >
                   <Typography
                     sx={{
                       ...theme.typography.subtext,
                       fontSize: { xs: 12, md: 16 },
                       textAlign: "left",
                     }}
                   >
                     {item.a}
                   </Typography>
                 </AccordionDetails>
               </Accordion>
             );
           })}
         </Box>
       </Container>

       <Box
         sx={{
           pt: 1,
           display: "flex",
           alignItems: "center",
           justifyContent: "center",
         }}
       >
         <CtaButton
           text={CONSULTATION_COPY.book}
           onClick={() => {
             navigate("/");
           }}
         />
       </Box>
     </Box>

     <Box sx={{ py: { xs: 6, md: 8 } }}>
       <Container
         maxWidth="md"
         sx={{
           textAlign: "center",
           display: "flex",
           flexDirection: "column",
           alignItems: "center",
           justifyContent: "center",
           gap: 3,
         }}
       >
         {/* Title */}
         <Typography sx={theme.typography.heading}>
           Already a Client?
         </Typography>

         {/* Supporting copy */}
         <Typography sx={theme.typography.subtext}>
           Access Nirva’s secure{" "}
           <strong>SimplePractice Client Portal</strong> to complete intake
           forms, review and sign documents, manage appointments, send secure
           messages, and join your telehealth sessions.
         </Typography>

         <CtaButton
           text="Access Client Portal"
           onClick={openSimplePracticeClientPortal}
         />
       </Container>
     </Box>

     <ReadyWhenYouAre />
   </Box>
 );
};

export default BookConsultation;
